import { type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';

import { describeError } from '@bluesphere/api-client';
import { SignInGate, signOut as identitySignOut } from '@bluesphere/identity-client';
import { PillButton } from '@bluesphere/ui-kit';

import { Logo } from './components/Logo';
import { IdleTimeout } from './auth/IdleTimeout';
import { identityConfig } from './auth/oidc';
import { SessionEndWatcher } from './auth/SessionEndWatcher';
import { useTenant } from './auth/useTenant';

interface AppProps {
  children: ReactNode;
}

/**
 * Top-level chrome: the sign-in gate, then the access gate, then the app.
 *
 * Sign-in is identity-client's `SignInGate` (Story 53, design r28):
 *   1. back from sign-in, react-oidc-context completes it;
 *   2. a tab the user signed out of (or idled out of, or whose session
 *      ended elsewhere) shows that, and does not sign straight back in;
 *   3. otherwise a silent sign-in on managed login's session, then the
 *      launcher hint, so opening MDR from another BlueSphere app shows no
 *      screen;
 *   4. otherwise the work-email screen, whose domain picks the
 *      organization's identity provider (`idp_identifier`).
 * The gate is styled through its `data-bsid` hooks (styles/index.css).
 *
 * Then `GET /me` decides what the signed-in user sees.
 *
 * **The access gate here is UX, not security.** The API refuses a caller
 * with no record, or a disabled one, on every route and is the only thing
 * that decides access. What the gate buys is that a user who can do
 * nothing is told so once, instead of being dropped into a console that
 * answers 403 to every action while looking like it is merely empty.
 *
 * Any enabled record is enough to enter (Story 22). What the user can then
 * DO still differs by role and scope — `useTenant().can()` hides what the
 * server would refuse — and the server still decides.
 */
export function App({ children }: AppProps) {
  return (
    <SignInGate
      config={identityConfig}
      product="BlueSphere MDR"
      loading={<SplashScreen message="Signing you in…" />}
    >
      <AccessGate>{children}</AccessGate>
    </SignInGate>
  );
}

function AccessGate({ children }: AppProps) {
  const auth = useAuth();
  const tenant = useTenant();

  if (tenant.status === 'loading') {
    return <SplashScreen message="Loading your access…" />;
  }

  if (tenant.status === 'error') {
    return (
      <SplashScreen
        message="Could not load your access"
        sub={describeError(tenant.error)}
        action={<PillButton onClick={tenant.refetch}>Try again</PillButton>}
      />
    );
  }

  // Signed in, but no user record, or a disabled one (403 `no_access`).
  if (tenant.status === 'no_access') {
    return (
      <SplashScreen
        message="No access"
        sub={
          'Your account is signed in but has no BlueSphere MDR user record, or it is disabled. ' +
          'Ask a Simulint Admin to add you on the Users page, then reload.'
        }
        action={
          <div className="flex flex-col items-center gap-3">
            <PillButton
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() =>
                void identitySignOut(() => auth.removeUser(), identityConfig, 'user', {
                  accessToken: auth.user?.access_token,
                })
              }
            >
              Sign out
            </PillButton>
            {tenant.email && (
              <span className="text-xs text-white/50">Signed in as {tenant.email}</span>
            )}
          </div>
        }
      />
    );
  }

  return (
    <>
      <SessionEndWatcher />
      <IdleTimeout />
      {children}
    </>
  );
}

function SplashScreen({
  message,
  sub,
  action,
}: {
  message: string;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 text-white">
      <div className="w-full max-w-md rounded-card bg-bluesphere-primary p-10 text-center shadow-modal">
        <div className="mb-6 flex justify-center">
          <Logo className="h-10 w-auto" />
        </div>
        <div className="mb-1 text-xs font-medium uppercase tracking-widest text-bluesphere-secondary">
          Managed Detection &amp; Response
        </div>
        <div className="mb-2 text-3xl font-bold">{message}</div>
        {sub && <div className="mb-6 text-sm text-white/70">{sub}</div>}
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}
